/**
 * COMMENTS ON MY ARTICLES — `listCommentsOnMyArticles` and `replyToComment`, both added by
 * decision-log row 141 applying row 140. The prototype draws the screen and its "Reply", no more.
 *
 * READ AND ANSWER ONLY. THERE IS NO HIDE AND NO REPORT ON THIS SCREEN, and their absence is a
 * RULED PROPERTY rather than a milestone: LOCKED D44 clause 1 survived the row-141 reversal
 * untouched. Do not add one.
 *
 * AND — THE PART THAT IS EASY TO GET WRONG BY BEING HELPFUL — A HIDDEN COMMENT IS ABSENT FROM THIS
 * LIST, exactly as it is absent from the public thread. That is what stops the cabinet being used
 * to find out what was hidden. So there is NO "hidden" state in this UI, no greyed row, no count
 * of hidden comments and no "some comments are not shown" line: every one of those would leak the
 * fact the design removed. A comment that vanishes between two reads simply vanishes.
 *
 * THE REPLY IS AN ORDINARY COMMENT. It writes a reader-kind comment at depth 1 carrying the
 * author's own subject — byte for byte the row their reply on the article page would have written.
 * `isArticleAuthor` is computed at read time and stored nowhere, and it is the only thing that
 * makes a writer's answer distinguishable in a thread.
 *
 * A REPLY MAY NOT HAVE A REPLY. `replyToComment` answers 409 REPLY_HAS_NO_REPLIES on a reply, so
 * the control is only offered on a root — the same rule, drawn rather than discovered.
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the prototype's Comments. One
 * card per article, each comment a thread with its replies under it - a regrouping of the loaded
 * rows, never a second read (./comment-groups.ts); "Awaiting your reply" on one with no reply;
 * the filter as two pills. And the author's own words read "You" on a gold wash - the owner's
 * decision 5 - where they read the neutral label beside a "You" badge; readers keep that label.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useCallback, useId, useMemo, useState } from 'react';
import { Reply } from '@moonx/ui';
import { describeProblem, useCabinetApi } from '../../features/cabinet/api';
import {
  ArticleLine,
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  LoadMore,
  LoadingState,
  PillChoice,
  RefreshingNote,
} from '../../features/cabinet/components';
import { TextareaWithEmoji, countGraphemes } from '../../features/cabinet/components/QuickEmoji';
import { useAction, useKeysetList } from '../../features/cabinet/state';
import { formatInstant } from '../../features/cabinet/format';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import type { CabinetComment, CabinetCommentPage, PublicId } from '../../features/cabinet/api';
import { groupByArticle, parentOf } from './comment-groups';
import type { ArticleComments, CommentThread } from './comment-groups';
import { useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';
import classes from './comments.module.css';

/**
 * The server's cap on a reply, `comment_reply_body_max` (2,000 at launch, decision-log row 146),
 * counted in code points (`countCodePoints`). The generated type erases it - `body` is `string` - so
 * the warning below is ADVISORY: it never blocks a send. Design 24, T21: the counter shows what a
 * reader sees, one emoji one character (`countGraphemes`), and the warning measures as the server
 * does, in code points, so it still matches the refusal (`overTheServerCap`, at the foot).
 */
const REPLY_SOFT_LIMIT = 2_000;

export interface CommentsRouteProps {
  /** Narrow to one article. The operation takes it; a route that has an id can pass it through. */
  articleId?: PublicId;
}

export function CommentsRoute({ articleId }: CommentsRouteProps) {
  const { t } = useTranslation('engagement');
  const api = useCabinetApi();
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | undefined>(undefined);

  const key = useMemo(
    () => `comments:${articleId ?? 'all'}:${unansweredOnly ? 'unanswered' : 'all'}`,
    [articleId, unansweredOnly],
  );

  const read = useCallback(
    (cursor: string | undefined, signal: AbortSignal) =>
      api.listCommentsOnMyArticles(
        {
          ...(articleId === undefined ? {} : { articleId }),
          ...(unansweredOnly ? { unansweredOnly: true } : {}),
          ...(cursor === undefined ? {} : { cursor }),
        },
        signal,
      ),
    [api, articleId, unansweredOnly],
  );

  const list = useKeysetList<CabinetComment, CabinetCommentPage>(read, key);

  const reply = useAction((commentId: PublicId, body: string) =>
    api.replyToComment(commentId, { body }),
  );

  const onReply = useCallback(
    async (commentId: PublicId, body: string) => {
      const written = await reply.run(commentId, body);
      if (written !== undefined) {
        setReplyingTo(undefined);
        // Re-read. The reply is a real row in the thread and the root's `hasAuthorReply` changes
        // with it, so the list is the only honest picture of what now exists.
        list.reload();
      }
    },
    [reply, list],
  );

  // Design 15: the rows loaded so far, one card per article (./comment-groups.ts). "Load more" adds
  // rows and they join their article's card. The unanswered filter is the one read that carries a
  // count (`CabinetCommentPage.total`, every waiting comment across pages); the full list has none.
  const groups = useMemo(() => groupByArticle(list.items), [list.items]);
  const waiting = unansweredOnly ? list.lastPage?.total : undefined;

  return (
    <Stack gap="lg">
      <CabinetPageHeader title={t('comments.title')} lede={ledeFor(unansweredOnly, waiting, t)} />

      {/* Reached from an article's own page: its comments alone, and the way to all of them. */}
      {articleId === undefined ? null : (
        <Group gap="xs" wrap="wrap">
          <Text size="sm" c="dimmed">
            {t('comments.oneArticle.note')}
          </Text>
          <InAppAnchor href="/comments" size="sm">
            {t('comments.oneArticle.showAll')}
          </InAppAnchor>
        </Group>
      )}

      <PillChoice
        label={t('comments.filter.label')}
        options={[
          { value: 'all', label: t('comments.filter.all') },
          { value: 'waiting', label: t('comments.filter.unanswered') },
        ]}
        value={unansweredOnly ? 'waiting' : 'all'}
        onChange={(value) => {
          setUnansweredOnly(value === 'waiting');
        }}
      />

      {reply.error === undefined ? null : (
        <Alert
          variant="light"
          role="alert"
          title={t('comments.replyFailed')}
          withCloseButton
          onClose={reply.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{describeProblem(reply.error)}</Text>
        </Alert>
      )}

      {list.isInitialLoad ? (
        <LoadingState label={t('comments.reading')} rows={4} />
      ) : list.status === 'error' && list.items.length === 0 ? (
        <ErrorState title={t('comments.failed')} error={list.error} onRetry={list.reload} />
      ) : list.items.length === 0 ? (
        <EmptyState
          headline={
            unansweredOnly ? t('comments.empty.repliedHeadline') : t('comments.empty.noneHeadline')
          }
          explanation={
            unansweredOnly
              ? t('comments.empty.repliedExplanation')
              : t('comments.empty.noneExplanation')
          }
          action={
            unansweredOnly ? (
              <Button
                variant="default"
                onClick={() => {
                  setUnansweredOnly(false);
                }}
              >
                {t('comments.empty.showAll')}
              </Button>
            ) : null
          }
        />
      ) : (
        <Stack gap="md">
          {list.status === 'error' ? (
            <ErrorState
              title={t('comments.lastReadFailed')}
              error={list.error}
              onRetry={list.reload}
            />
          ) : null}
          {list.isLoadingMore ? <RefreshingNote label={t('comments.loadingMore')} /> : null}

          {groups.map((group) => (
            <ArticleCard
              key={group.articleId}
              group={group}
              waitingOnly={unansweredOnly}
              hasMore={list.hasMore}
              replyingTo={replyingTo}
              isSending={reply.isRunning}
              onStartReply={setReplyingTo}
              onCancelReply={() => {
                setReplyingTo(undefined);
              }}
              onSend={onReply}
            />
          ))}

          <LoadMore
            hasMore={list.hasMore}
            isLoading={list.isLoadingMore}
            noun={t('comments.noun')}
            count={list.items.length}
            onLoadMore={list.loadMore}
          />
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The line under the title. A number only where the read served one: the unanswered filter's
 * `total`. The full list carries no count, so its line has none - never the length of a page.
 */
function ledeFor(
  waitingOnly: boolean,
  waiting: number | undefined,
  t: TFunction<'engagement'>,
): string {
  if (!waitingOnly) {
    return t('comments.lede.all');
  }
  if (waiting === undefined) {
    return t('comments.lede.waitingUncounted');
  }
  if (waiting === 0) {
    return t('comments.lede.nothingWaiting');
  }
  return t('comments.lede.waiting', { count: waiting });
}

/**
 * The card's count, of the rows loaded so far. "So far" while more pages remain: a later page may
 * hold more of this article's comments, and the count must not read as the article's total.
 */
function countWords(
  count: number,
  waitingOnly: boolean,
  hasMore: boolean,
  t: TFunction<'engagement'>,
): string {
  if (waitingOnly) {
    return hasMore
      ? t('comments.card.unansweredSoFar', { count })
      : t('comments.card.unanswered', { count });
  }
  return hasMore ? t('comments.card.countSoFar', { count }) : t('comments.card.count', { count });
}

/** Who wrote it, as the card says it: "You" for the author's own words, the read-time label else. */
function nameOf(comment: CabinetComment, t: TFunction<'engagement'>): string {
  // The owner's decision 5 (design 15). `author` is the neutral label "rendered from nothing stored
  // on the row" - WC-SN-09: no reader is ever named, so a reader's label is shown as it came.
  return comment.isArticleAuthor ? t('comments.you') : comment.author;
}

/**
 * The tints and the inks on them, written inline so a spec reads back what was drawn and derives its
 * contrast on the card (test/routes/comments/comments.spec.tsx; the method is
 * test/harness/contrast.ts). Gold words on the gold wash take the HOVER gold, which reads on a tint
 * in both schemes; "Awaiting your reply" keeps its red wash and writes its words in `--mx-text`.
 */
const MINE_WASH = { backgroundColor: 'oklch(var(--mx-accent) / 0.07)' } as const;
const MINE_INK = { color: 'oklch(var(--mx-accent-hover))' } as const;
const NEEDS_REPLY = {
  backgroundColor: 'oklch(var(--mx-danger) / 0.14)',
  color: 'oklch(var(--mx-text))',
} as const;

function initialOf(name: string): string {
  return (Array.from(name.trim())[0] ?? '?').toUpperCase();
}

interface ReplyControls {
  replyingTo: string | undefined;
  /** A reply is being sent - the card that sent it shows it. */
  isSending: boolean;
  onStartReply: (commentId: string) => void;
  onCancelReply: () => void;
  onSend: (commentId: PublicId, body: string) => Promise<void>;
}

interface ArticleCardProps extends ReplyControls {
  group: ArticleComments;
  waitingOnly: boolean;
  hasMore: boolean;
}

function ArticleCard({ group, waitingOnly, hasMore, ...controls }: ArticleCardProps) {
  const { t } = useTranslation('engagement');
  const headingId = useId();
  return (
    <section className={classes.article} aria-labelledby={headingId}>
      <div className={classes.articleHead}>
        <ArticleLine
          title={group.articleTitle ?? t('comments.untitled')}
          webPath={group.articleWebPath}
          coverUrl={group.articleCoverUrl}
          headingId={headingId}
        >
          <div className={classes.articleMeta}>
            {countWords(group.count, waitingOnly, hasMore, t)}
          </div>
        </ArticleLine>
      </div>
      <div className={classes.articleBody}>
        {group.threads.map((thread) => (
          <ThreadView key={thread.comment.commentId} thread={thread} {...controls} />
        ))}
      </div>
    </section>
  );
}

interface ThreadViewProps extends ReplyControls {
  thread: CommentThread;
}

function ThreadView({ thread, ...controls }: ThreadViewProps) {
  if (parentOf(thread.comment) !== undefined) {
    // A reply whose comment is not loaded: its own place, stepped in as a reply, and nothing said
    // about the comment above it (./comment-groups.ts).
    return (
      <div className={classes.entry}>
        <div className={classes.replies}>
          <ReplyEntry comment={thread.comment} />
        </div>
      </div>
    );
  }
  return <RootEntry thread={thread} {...controls} />;
}

function RootEntry({
  thread,
  replyingTo,
  isSending,
  onStartReply,
  onCancelReply,
  onSend,
}: ThreadViewProps) {
  const { t } = useTranslation('engagement');
  const { comment, replies } = thread;
  const [draft, setDraft] = useState('');
  const mine = comment.isArticleAuthor;
  const name = nameOf(comment, t);
  const isReplying = replyingTo === comment.commentId;
  const canReply = !comment.hasAuthorReply;
  // Waiting on the author: a reader's comment with no reply from them. Never on their own words.
  const needsReply = !comment.hasAuthorReply && !mine;
  // The answer is usually drawn right under it; the line says so only when it is not.
  const answerShown = replies.some((candidate) => candidate.isArticleAuthor);

  return (
    <article className={classes.entry}>
      <div
        className={mine ? `${classes.comment} ${classes.mine}` : classes.comment}
        style={mine ? MINE_WASH : undefined}
      >
        <span className={classes.initial} aria-hidden="true">
          {initialOf(name)}
        </span>
        <div className={classes.main}>
          <div className={classes.byline}>
            <span
              className={mine ? classes.nameMine : classes.name}
              style={mine ? MINE_INK : undefined}
            >
              {name}
            </span>
            <span className={classes.when}>{formatInstant(comment.createdAt)}</span>
            {needsReply ? (
              <span className={classes.needsReply} style={NEEDS_REPLY}>
                {t('comments.awaitingReply')}
              </span>
            ) : null}
          </div>
          <p className={classes.body}>{comment.body}</p>

          {isReplying ? (
            <Stack gap="xs" mt="sm">
              <TextareaWithEmoji
                label={t('comments.reply.label')}
                description={t('comments.reply.description')}
                value={draft}
                autoFocus
                minRows={3}
                onChange={(value) => {
                  setDraft(value);
                }}
              />
              <Group justify="space-between">
                <Text
                  size="xs"
                  c={overTheServerCap(draft) ? 'oklch(var(--mx-warning-ink))' : 'dimmed'}
                >
                  {overTheServerCap(draft)
                    ? t('comments.reply.tooLong')
                    : t('comments.reply.characters', { count: countGraphemes(draft) })}
                </Text>
                <Group gap="xs">
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => {
                      setDraft('');
                      onCancelReply();
                    }}
                  >
                    {t('comments.reply.cancel')}
                  </Button>
                  <Button
                    size="compact-sm"
                    loading={isSending}
                    disabled={draft.trim().length === 0}
                    onClick={() => {
                      void (async () => {
                        await onSend(comment.commentId, draft.trim());
                        setDraft('');
                      })();
                    }}
                  >
                    {t('comments.reply.post')}
                  </Button>
                </Group>
              </Group>
            </Stack>
          ) : canReply ? (
            <div className={classes.after}>
              <button
                type="button"
                className={classes.replyAction}
                onClick={() => {
                  onStartReply(comment.commentId);
                }}
              >
                <Reply size={13} aria-hidden="true" />
                {t('comments.reply.start')}
              </button>
            </div>
          ) : answerShown ? null : (
            <div className={`${classes.after} ${classes.quiet}`}>{t('comments.reply.replied')}</div>
          )}
        </div>
      </div>

      {replies.length === 0 ? null : (
        <div className={classes.replies}>
          {replies.map((answer) => (
            <ReplyEntry key={answer.commentId} comment={answer} />
          ))}
        </div>
      )}
    </article>
  );
}

/** A reply in a thread: smaller, and on the gold wash when it is the author's own. */
function ReplyEntry({ comment }: { comment: CabinetComment }) {
  const { t } = useTranslation('engagement');
  const mine = comment.isArticleAuthor;
  const name = nameOf(comment, t);
  return (
    <article
      className={mine ? `${classes.reply} ${classes.mine}` : classes.reply}
      style={mine ? MINE_WASH : undefined}
    >
      <span
        className={
          mine
            ? `${classes.initial} ${classes.initialSmall} ${classes.initialMine}`
            : `${classes.initial} ${classes.initialSmall}`
        }
        aria-hidden="true"
      >
        {initialOf(name)}
      </span>
      <div className={classes.main}>
        <div className={classes.byline}>
          <span
            className={mine ? classes.nameMine : classes.name}
            style={mine ? MINE_INK : undefined}
          >
            {name}
          </span>
          <span className={classes.when}>{formatInstant(comment.createdAt)}</span>
        </div>
        <p className={classes.body}>{comment.body}</p>
      </div>
    </article>
  );
}

/** Past `REPLY_SOFT_LIMIT` as the server counts, in code points: a heart is two of them. */
function overTheServerCap(draft: string): boolean {
  return Array.from(draft).length > REPLY_SOFT_LIMIT;
}
